
import React, { ReactNode } from 'react';
import { CardHeader, CardContent, Typography, Box, Chip, Tooltip } from '@mui/material';
import { TrendingUp, TrendingDown, Refresh } from '@mui/icons-material';
import { INVITE_DRAWER_TYPE, PATIENT_LIST_VIEW } from 'pages/Dashboard/enums';
import { PatientObj } from 'pages/Dashboard/types/patient.types';
import { COLOR, formatDOB, formatActivity, formatLastUpdated, getDaysDifferenceFromToday } from 'pages/Dashboard/components/PatientCardUtils';
import HLButton from 'components/HLButton';
import { green, orange } from 'theme';
import { deepOrange, lightBlue, red } from '@mui/material/colors';
import HLCard from 'components/HLCard';
import { splitStringToInitialAndOtherCharacters } from 'pages/Dashboard/components/NameText';
import { formatPhoneNumber } from 'utils/phoneUtils';
import ResolveInviteForm from 'pages/Dashboard/components/ResolveInviteForm';
import ResendInviteForm from 'pages/Dashboard/components/ResendInviteForm';
import { Link } from 'react-router-dom';

interface PatientInfoRowProps {
  items: ReactNode[];
}

function PatientInfoRow({ items }: PatientInfoRowProps) {
  if (!items.length) return null;
  return (
    <Box display='flex' alignItems='center' gap={1}>
      {items.map((item, idx) => (
        <>
          {idx > 0 && (
            <Typography variant='caption' color='text.secondary'>
              &#8226;
            </Typography>
          )}
          {item}
        </>
      ))}
    </Box>
  );
}

interface PatientCardProps {
  patient: PatientObj;
  status: PATIENT_LIST_VIEW;
  href?: string;
  onConfirm: (patient: PatientObj) => void;
  onViewHealthRecords: (e: React.MouseEvent<HTMLButtonElement>, patientId?: string) => void;
  onRefreshIconClick: (e: React.MouseEvent<HTMLDivElement>, patient: PatientObj) => void;
  onPendingPatientClick: (
    e: React.MouseEvent<HTMLDivElement>,
    shouldResendInvite: boolean,
    setDrawerItem: (item: INVITE_DRAWER_TYPE) => void
  ) => void;
}

export function getThirtyDayStatusChipAndTooltip(status?: PatientObj['thirtyDayStatus']) {
  let chip = null;
  let tooltipText = '';
  let icon = null;
  switch (status) {
    case 'UP':
      chip = (
        <Chip
          icon={<TrendingUp fontSize='small' />}
          size='small'
          label='improving'
          sx={(theme) => ({
            background: green[50],
            color: green[800],
            ...theme.typography.caption,
            '& .MuiChip-icon': {
              color: green[800],
            },
          })}
        />
      );
      tooltipText = 'An improvement in pleasantness or energy, without a decline in the other';
      icon = <TrendingUp sx={{ color: green[800] }} />;
      break;
    case 'DOWN':
      chip = (
        <Chip
          label='declining'
          size='small'
          icon={<TrendingDown fontSize='small' />}
          sx={(theme) => ({
            background: red[50],
            color: deepOrange[900],
            ...theme.typography.caption,
            '& .MuiChip-icon': {
              color: deepOrange[900],
            },
          })}
        />
      );
      tooltipText = 'A decline in pleasantness or energy';
      icon = <TrendingDown sx={{ color: deepOrange[900] }} />;
      break;
    case 'NEUTRAL':
    case 'NO_DATA':
    case 'NOT_ENOUGH_DATA':
      chip = null;
      tooltipText = 'Not enough data to determine a change';
      icon = null;
      break;
    default:
      chip = null;
      tooltipText = '';
      icon = null;
  }
  return {
    chip,
    tooltip: (
      <Box sx={{ display: 'flex', gap: '8px', width: 400, alignItems: 'center', paddingRight: 2 }}>
        {icon}
        <Box style={{ display: 'flex', flexDirection: 'column' }}>
          <Typography variant='body2' fontSize={16}>{tooltipText}</Typography>
        </Box>
      </Box>
    ),
  };
}

export function PatientCard({
  patient,
  status,
  href,
  onConfirm,
  onViewHealthRecords,
  onRefreshIconClick,
  onPendingPatientClick,
}: PatientCardProps) {
  const [drawerItem, setDrawerItem] = React.useState('');

  const isPending = status === PATIENT_LIST_VIEW.PENDING;
  const dob = formatDOB(patient.dateOfBirth);
  const id = patient.id || '';
  const needsConfirmation = patient.approvalStatus === 'PATIENT_APPROVAL_STATUS_PENDING' && !isPending;
  const activity = formatActivity(patient.patientActivity);
  const lastUpdated = patient?.refreshHistory?.scheduledOn
    ? formatLastUpdated(patient?.refreshHistory?.scheduledOn)
    : null;
  const cardBg = needsConfirmation ? COLOR.blueBg : COLOR.white;

  const formattedPhoneNumber = formatPhoneNumber(patient.phoneNumber);

  const phoneNumberString = `${patient.alias} (${String(
    formattedPhoneNumber || 'No Number Available',
  )})`;

  const { firstNameInitial,
    otherFirstNameCharacters,
    lastNameInitial,
    otherLastNameCharacters } = splitStringToInitialAndOtherCharacters(patient.name);
  const formattedName = `${firstNameInitial}${otherFirstNameCharacters} ${lastNameInitial}${otherLastNameCharacters}`;

  const name = !patient?.name?.firstName ? phoneNumberString : formattedName;

  let actionButton = null;
  let shouldResendInvite = false;
  if (isPending) {
    if (
      [
        'PENDING_INVITE_DELIVERY',
        'PENDING_PATIENT_SIGNUP',
        'ONBOARDING_STARTED',
        'PREPARING_INVITE_DELIVERY',
      ].includes(String(patient.onboardingMetadata?.status))
    ) {
      shouldResendInvite = true;
      actionButton = (
        <HLButton color='primary' variant='text' size='small' onClick={() => setDrawerItem(INVITE_DRAWER_TYPE.RESEND_INVITE)}>
          Resend invite
        </HLButton>
      );
    }
    else if (String(patient.onboardingMetadata?.status) === 'INVITE_DELIVERY_FAILED') {
      shouldResendInvite = false;
      actionButton = (
        <HLButton color='primary' variant='text' size='small' onClick={() => setDrawerItem(INVITE_DRAWER_TYPE.RESOLVE_INVITE)}>
          Resolve error
        </HLButton>
      );
    }
  }
  else if (needsConfirmation) {
    actionButton = (
      <HLButton
        variant='text'
        sx={{ color: 'primary.main', fontWeight: 500 }}
        onClick={() => onConfirm(patient)}
      >
        Confirm patient
      </HLButton>
    );
  }
  else {
    actionButton = (
      <Box display='flex' alignItems='center' gap={1}>
        {patient?.refreshHistory?.scheduledOn
        && (() => {
          const diffInDays = getDaysDifferenceFromToday(patient.refreshHistory.scheduledOn);
          if (diffInDays && diffInDays >= 60) {
            return (
              <Box
                onClick={(e) => onRefreshIconClick(e, patient)}
                width={24}
                height={24}
                display='flex'
                alignItems='center'
                justifyContent='center'
                borderRadius='50%'
                sx={{ background: lightBlue[50] }}
                zIndex={20}
              >
                <Refresh sx={{ color: 'primary.main', fontSize: 16 }} />
              </Box>
            );
          }
          return null;
        })()}
        <HLButton
          size='small'
          variant='text'
          sx={{ color: 'primary.main', fontWeight: 500 }}
          onClick={(e) => onViewHealthRecords(e, patient.id)}
        >
          View health records
        </HLButton>
      </Box>
    );
  }

  let chips = null;
  if (isPending) {
    chips = String(patient.onboardingMetadata?.status) === 'INVITE_DELIVERY_FAILED' ? (
      <Chip
        label='error'
        color='error'
        size='small'
        sx={{ mt: 1, background: red[50], color: red[900] }}
      />
    ) : (
      <Chip
        label='pending'
        color='warning'
        size='small'
        sx={{ mt: 1, background: orange[50], color: red[900] }}
      />
    );
  }
  else {
    const { chip, tooltip } = getThirtyDayStatusChipAndTooltip(patient.thirtyDayStatus);
    if (chip) {
      chips = (
        <Box display='flex' alignItems='center' gap={1} mt={1}>
          <Tooltip
            placement='top'
            title={tooltip}
            componentsProps={{
              tooltip: {
                sx: {
                  backgroundColor: '#F2F2F2',
                  color: 'black',
                  boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)',
                  borderRadius: '4px',
                  padding: '8px',
                  height: 'auto',
                  maxWidth: '400px',
                },
              },
              arrow: {
                sx: {
                  color: '#F2F2F2',
                },
              },
            }}
            arrow
          >
            <span style={{ display: 'inline-flex', alignItems: 'center' }}>{chip}</span>
          </Tooltip>
        </Box>
      );
    }
  }

  const onPatientClickHandler = (e: React.MouseEvent<HTMLDivElement>) => {
    if (isPending) {
      onPendingPatientClick(e, shouldResendInvite, setDrawerItem);
      return;
    }
    else if (!needsConfirmation) {
      return;
    }

    onConfirm(patient);
    e.stopPropagation();
  };

  const closeDrawer = React.useCallback(() => {
    setDrawerItem('');
  }, []);

  return (
    <>
      {drawerItem === INVITE_DRAWER_TYPE.RESOLVE_INVITE && (
        <ResolveInviteForm
          patient={patient}
          drawerItem={drawerItem}
          closeDrawer={closeDrawer}
          drawerMode
        />
      )}
      {drawerItem === INVITE_DRAWER_TYPE.RESEND_INVITE && (
        <ResendInviteForm
          patient={patient}
          drawerItem={drawerItem}
          closeDrawer={closeDrawer}
          drawerMode
        />
      )}
      <HLCard
        key={id}
        sx={(theme) => ({
          backgroundColor: cardBg,
          borderRadius: 2,
          cursor: 'pointer',
          ':hover': {
            border: `1px solid ${theme.palette.primary.main}`,
            '& .MuiCardHeader-title': {
              textDecoration: 'underline',
            },
          },
        })}
        component={href ? Link : undefined}
        to={href}
        onClick={onPatientClickHandler}
      >
        <CardHeader
          title={name}
          subheader={(
            <PatientInfoRow
              items={[
                dob && (
                <Typography key='dob' variant='caption' color='text.secondary'>
                  {dob}
                </Typography>
                ),
                activity && (
                <Typography key='activity' variant='caption' color='text.secondary'>
                  {activity}
                </Typography>
                ),
                isPending && patient.dateAdded && (
                <React.Fragment key='inviteInfo'>
                  <Typography key='invite' variant='caption' color='text.secondary'>
                    Invite sent
                    {' '}
                    <Typography variant='caption' component='span' fontWeight={700}>{formatLastUpdated(patient.dateAdded)}</Typography>
                  </Typography>
                </React.Fragment>
                ),
                needsConfirmation && (
                <Typography key='selfSignUp' variant='caption' color='text.secondary'>
                  Self sign up
                </Typography>
                ),
              ].filter(Boolean) as ReactNode[]}
            />
          )}
          titleTypographyProps={{
            variant: 'body1',
            color: 'primary.main',
            fontWeight: 600,
          }}
          action={actionButton}
          sx={{ pb: 0, px: 1.5 }}
        />
        <CardContent sx={{ px: 1.5, pt: 0, ':last-child': { pb: 2 } }}>
          {!needsConfirmation && !isPending && lastUpdated && (
          <Box display='flex' alignItems='center' gap={1} mt={1}>
            <Typography variant='caption' color='text.secondary'>
              Health Records last updated
              {' '}
              <span style={{ fontWeight: 700 }}>{lastUpdated}</span>
            </Typography>
          </Box>
          )}
          {needsConfirmation && (
          <Box display='flex' alignItems='center' gap={1} mt={1}>
            {' '}
            <Typography
              component='p'
              variant='body2'
              fontWeight={700}
              sx={{ color: 'primary.main' }}
            >
              confirm patient
              {' '}
              <Typography
                component='span'
                variant='body2'
                fontWeight={700}
                sx={{ color: 'text.secondary' }}
              >
                to view
              </Typography>
            </Typography>
          </Box>
          )}
          <Box display='flex' alignItems='center' gap={1}>
            {chips}
          </Box>
        </CardContent>
      </HLCard>
    </>
  );
}

export default PatientCard;
